import { type ComponentType, FC, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useSituation, useUser } from 'modules/user'
import { useAuth } from 'modules/auth'
import { useWebView } from 'modules/web-view'
import { PageLoader } from 'layouts/loaders'
import { WebViewPostMessageType } from '@types'

export const withSituation = <P extends object>(
  Component: ComponentType<P>
): FC<P> => {
  function WithSituation(props: P) {
    const { isAuthReady, auth } = useAuth()
    const {
      fetch: fetchSituation,
      convertLegalPersonToCompany,
      isSituationReady,
      isLoading: situationIsLoading,
    } = useSituation()
    const {
      user,
      fetch: fetchUser,
      isUserReady,
      mergeCompanies,
      isLoading: userIsLoading,
    } = useUser()
    const history = useHistory()
    const { sendMessage } = useWebView()

    useEffect(() => {
      history.listen(() => {
        if (history.action === 'POP') {
          fetchSituation()
        }
      })
    }, [])

    useEffect(() => {
      if (isAuthReady() && isUserReady() && !isSituationReady()) {
        sendMessage({
          type: WebViewPostMessageType.LOGIN_AUTHENTICATED,
          data: {
            ...user.data,
            authId: auth.data.uid,
            token: auth.data.token,
          },
        })

        fetchSituation((response) => {
          mergeCompanies(
            convertLegalPersonToCompany(response.legal_person_situation)
          )
        })
      }
    }, [user.status])

    useEffect(() => {
      if (isAuthReady() && !isUserReady()) {
        fetchUser()
      }
    }, [auth.status])

    if (situationIsLoading() || userIsLoading()) return <PageLoader />

    return <Component {...props} />
  }

  return WithSituation
}
